import type { Box } from './model'
import { ASK } from './area'

/**
 * A PICTURE OF AN AREA, as the person sees it (owner, 2026-10-31: 做一個截圖 … Agent 就可以直接去查看這個元件 … 的視覺).
 *
 * The board is SVG, so the picture is the board's own drawing cut to the area: the layer of items is copied (never
 * the selection, handles or the question's own window), each picture on the board is written into the copy as data
 * (a drawing shown as an image may load nothing from outside itself), and the copy is drawn onto a canvas a little
 * larger than the area — with the dashed box on it, so the agent sees what was boxed and what is only around it.
 *
 * It is a help, never a condition: a browser that cannot draw it answers null, and the question goes without it
 * (the items it names still say what is there).
 */

/** Room round the box, in board pixels: what is beside the area is context, not part of it. */
const MARGIN = 24
/** The longest side the picture keeps, in pixels. */
const MAX_SIDE = 1600

function asDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(r.error ?? new Error('unreadable'))
    r.readAsDataURL(blob)
  })
}

/** The copy's pictures, written in as data. One that will not read is left out rather than stopping the picture. */
async function inlinePictures(layer: Element): Promise<void> {
  const images = [...layer.querySelectorAll('image')]
  await Promise.all(
    images.map(async (img) => {
      const href = img.getAttribute('href') ?? img.getAttribute('xlink:href') ?? ''
      if (!href || href.startsWith('data:')) return
      try {
        const res = await fetch(href)
        img.setAttribute('href', await asDataUrl(await res.blob()))
      } catch {
        img.remove()
      }
    }),
  )
}

/** The SVG text of the area: the copied layer under a view of the area and its margin, on white, with the box drawn. */
function areaSvg(layerMarkup: string, area: Box, scale: number): string {
  const vx = area.x - MARGIN
  const vy = area.y - MARGIN
  const vw = area.w + 2 * MARGIN
  const vh = area.h + 2 * MARGIN
  const w = Math.max(1, Math.round(vw * scale))
  const h = Math.max(1, Math.round(vh * scale))
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${w}" height="${h}" viewBox="${vx} ${vy} ${vw} ${vh}">` +
    `<rect x="${vx}" y="${vy}" width="${vw}" height="${vh}" fill="#ffffff"/>` +
    layerMarkup +
    `<rect x="${area.x}" y="${area.y}" width="${area.w}" height="${area.h}" fill="none" stroke="${ASK}" stroke-width="${2 / scale}" stroke-dasharray="${8 / scale} ${5 / scale}"/>` +
    `</svg>`
  )
}

/** How much larger than the board the picture is drawn: twice, unless that passes MAX_SIDE. */
function pictureScale(area: Box): number {
  return Math.max(0.25, Math.min(2, MAX_SIDE / (Math.max(area.w, area.h) + 2 * MARGIN)))
}

/** A PNG of the area, or null when this browser cannot make one. `layer` is the board's layer of items. */
export async function areaPicture(layer: SVGGElement | null, area: Box): Promise<Blob | null> {
  if (!layer) return null
  try {
    const copy = layer.cloneNode(true) as SVGGElement
    copy.removeAttribute('transform')
    await inlinePictures(copy)
    const scale = pictureScale(area)
    const svg = areaSvg(new XMLSerializer().serializeToString(copy), area, scale)
    const img = new Image()
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
    await img.decode()
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth || Math.round((area.w + 2 * MARGIN) * scale)
    canvas.height = img.naturalHeight || Math.round((area.h + 2 * MARGIN) * scale)
    const g = canvas.getContext('2d')
    if (!g) return null
    g.drawImage(img, 0, 0, canvas.width, canvas.height)
    return await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'))
  } catch {
    return null
  }
}
